import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const STATUS_LABELS = {
  draft: 'Kladde',
  sent: 'Sendt',
  paid: 'Betalt',
  cancelled: 'Annulleret'
};

const STATUS_COLORS = {
  draft: '#94a3b8',
  sent: '#3b82f6',
  paid: '#16a34a',
  cancelled: '#dc2626'
};

export default function InvoiceDetail() {
  const { id } = useParams();
  const { apiFetch } = useAuth();
  const navigate = useNavigate();
  const [invoice, setInvoice] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [sending, setSending] = useState(false);
  const [showPayModal, setShowPayModal] = useState(false);
  const [paymentAccountId, setPaymentAccountId] = useState('');
  const [paymentDate, setPaymentDate] = useState(new Date().toISOString().split('T')[0]);
  const [paying, setPaying] = useState(false);

  useEffect(() => {
    Promise.all([
      apiFetch(`/api/invoices/${id}`),
      apiFetch('/api/accounts')
    ]).then(([inv, accs]) => {
      setInvoice(inv);
      setAccounts(accs);
      // Default to bank account for payment
      const bank = accs.find(a => a.account_number === '6610');
      if (bank) setPaymentAccountId(bank.id.toString());
      setLoading(false);
    }).catch(err => {
      setError(err.message);
      setLoading(false);
    });
  }, [id, apiFetch]);

  const handleSend = async () => {
    setError(''); setSuccess('');
    setSending(true);
    try {
      const result = await apiFetch(`/api/invoices/${id}/send`, { method: 'POST' });
      setSuccess(result.message);
      // Reload invoice
      const inv = await apiFetch(`/api/invoices/${id}`);
      setInvoice(inv);
    } catch (err) {
      setError(err.message);
    }
    setSending(false);
  };

  const handlePay = async () => {
    setError(''); setSuccess('');
    setPaying(true);
    try {
      const result = await apiFetch(`/api/invoices/${id}/pay`, {
        method: 'POST',
        body: { payment_account_id: parseInt(paymentAccountId), payment_date: paymentDate }
      });
      setSuccess(result.message);
      setShowPayModal(false);
      const inv = await apiFetch(`/api/invoices/${id}`);
      setInvoice(inv);
    } catch (err) {
      setError(err.message);
    }
    setPaying(false);
  };

  const handleDelete = async () => {
    if (!window.confirm('Er du sikker på at du vil slette denne faktura?')) return;
    try {
      await apiFetch(`/api/invoices/${id}`, { method: 'DELETE' });
      navigate('/app/fakturaer');
    } catch (err) {
      setError(err.message);
    }
  };

  const fetchPdfBlob = async () => {
    const token = localStorage.getItem('token');
    const API_BASE = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:3001';
    const res = await fetch(`${API_BASE}/api/invoices/${id}/pdf`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    if (!res.ok) throw new Error('Kunne ikke hente PDF.');
    return res.blob();
  };

  const downloadPdf = async () => {
    try {
      const blob = await fetchPdfBlob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Faktura-${invoice.invoice_number}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.message);
    }
  };

  const printPdf = async () => {
    try {
      const blob = await fetchPdfBlob();
      const url = URL.createObjectURL(blob);
      const printWindow = window.open(url, '_blank');
      if (printWindow) {
        printWindow.addEventListener('load', () => {
          printWindow.print();
          URL.revokeObjectURL(url);
        });
      }
    } catch (err) {
      setError(err.message);
    }
  };

  if (loading) return <div className="loading">Indlæser...</div>;
  if (!invoice) return <div className="alert alert-error">Faktura ikke fundet.</div>;

  const paymentAccounts = accounts.filter(a => a.type === 'asset' && a.account_number !== '6410');

  return (
    <div>
      <div className="page-header">
        <h1>Faktura #{invoice.invoice_number}</h1>
        <Link to="/app/fakturaer" className="btn btn-secondary no-print">Tilbage</Link>
      </div>

      {error && <div className="alert alert-error no-print">{error}</div>}
      {success && <div className="alert alert-success no-print">{success}</div>}

      {/* Actions */}
      <div className="no-print" style={{display: 'flex', gap: '0.5rem', marginBottom: '1.5rem', flexWrap: 'wrap'}}>
        <button className="btn btn-secondary" onClick={printPdf}>
          Print
        </button>
        <button className="btn btn-secondary" onClick={downloadPdf}>
          Download PDF
        </button>

        {(invoice.status === 'draft' || invoice.status === 'sent') && (
          <button className="btn btn-primary" onClick={handleSend} disabled={sending}>
            {sending ? 'Sender...' : invoice.status === 'draft' ? 'Send faktura' : 'Send igen'}
          </button>
        )}

        {(invoice.status === 'sent' || invoice.status === 'draft') && (
          <button className="btn btn-primary" style={{background: '#16a34a'}}
            onClick={() => setShowPayModal(true)}>
            Marker som betalt
          </button>
        )}

        {invoice.status === 'draft' && (
          <button className="btn btn-danger" onClick={handleDelete}>Slet</button>
        )}
      </div>

      {/* Invoice details */}
      <div className="card" style={{marginBottom: '1.5rem'}}>
        <div style={{display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: '1rem'}}>
          <div>
            <h3 style={{marginBottom: '0.5rem'}}>Modtager</h3>
            <p style={{fontWeight: 600, marginBottom: '0.25rem'}}>{invoice.recipient_name}</p>
            {invoice.recipient_address && <p style={{color: '#64748b', fontSize: '0.9rem'}}>{invoice.recipient_address}</p>}
            {invoice.recipient_email && <p style={{color: '#64748b', fontSize: '0.9rem'}}>{invoice.recipient_email}</p>}
          </div>
          <div style={{textAlign: 'right'}}>
            <div style={{marginBottom: '0.5rem'}}>
              <span style={{
                display: 'inline-block', padding: '4px 14px', borderRadius: '16px',
                fontSize: '0.85rem', fontWeight: 700,
                background: STATUS_COLORS[invoice.status] + '18',
                color: STATUS_COLORS[invoice.status]
              }}>
                {STATUS_LABELS[invoice.status]}
              </span>
            </div>
            <p style={{fontSize: '0.9rem', color: '#64748b'}}>
              Dato: {new Date(invoice.date).toLocaleDateString('da-DK')}
            </p>
            <p style={{fontSize: '0.9rem', color: '#64748b'}}>
              Forfald: {new Date(invoice.due_date).toLocaleDateString('da-DK')}
            </p>
            {invoice.sent_at && (
              <p style={{fontSize: '0.85rem', color: '#3b82f6'}}>
                Sendt: {new Date(invoice.sent_at).toLocaleString('da-DK')}
              </p>
            )}
            {invoice.paid_at && (
              <p style={{fontSize: '0.85rem', color: '#16a34a'}}>
                Betalt: {new Date(invoice.paid_at).toLocaleString('da-DK')}
              </p>
            )}
          </div>
        </div>

        {invoice.description && (
          <div style={{marginTop: '1rem', padding: '0.75rem', background: '#f8fafc', borderRadius: '6px'}}>
            <strong>Beskrivelse:</strong> {invoice.description}
          </div>
        )}
      </div>

      {/* Invoice lines */}
      <div className="card" style={{marginBottom: '1.5rem'}}>
        <h3 style={{marginBottom: '1rem'}}>Fakturalinjer</h3>
        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th>Beskrivelse</th>
                <th style={{textAlign: 'right'}}>Antal</th>
                <th style={{textAlign: 'right'}}>Stykpris</th>
                <th style={{textAlign: 'right'}}>Beløb</th>
              </tr>
            </thead>
            <tbody>
              {(invoice.lines || []).map((line, i) => (
                <tr key={i}>
                  <td>{line.description}</td>
                  <td style={{textAlign: 'right'}}>{parseFloat(line.quantity).toLocaleString('da-DK')}</td>
                  <td style={{textAlign: 'right'}}>{parseFloat(line.unit_price).toLocaleString('da-DK', {minimumFractionDigits: 2})} kr.</td>
                  <td style={{textAlign: 'right', fontWeight: 600}}>{parseFloat(line.amount).toLocaleString('da-DK', {minimumFractionDigits: 2})} kr.</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr style={{borderTop: '2px solid #e2e8f0'}}>
                <td colSpan={3} style={{textAlign: 'right', fontWeight: 700, fontSize: '1.1rem'}}>I alt:</td>
                <td style={{textAlign: 'right', fontWeight: 700, fontSize: '1.1rem'}}>
                  {parseFloat(invoice.total_amount).toLocaleString('da-DK', {minimumFractionDigits: 2})} kr.
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      {/* Linked vouchers */}
      {(invoice.voucher_id || invoice.payment_voucher_id) && (
        <div className="card no-print">
          <h3 style={{marginBottom: '0.75rem'}}>Bogføring</h3>
          {invoice.voucher_id && (
            <p>
              Fakturabilag: <Link to={`/app/bilag/${invoice.voucher_id}`}>Vis bilag</Link>
            </p>
          )}
          {invoice.payment_voucher_id && (
            <p>
              Betalingsbilag: <Link to={`/app/bilag/${invoice.payment_voucher_id}`}>Vis bilag</Link>
            </p>
          )}
        </div>
      )}

      {/* Pay modal */}
      {showPayModal && (
        <div className="modal-overlay" onClick={() => setShowPayModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h2>Registrer betaling</h2>
            <p style={{color: '#64748b', marginBottom: '1rem'}}>
              Faktura #{invoice.invoice_number} — {parseFloat(invoice.total_amount).toLocaleString('da-DK', {minimumFractionDigits: 2})} kr.
            </p>
            <div className="form-group">
              <label>Betalingsdato</label>
              <input type="date" className="form-control" value={paymentDate}
                onChange={e => setPaymentDate(e.target.value)} />
            </div>
            <div className="form-group">
              <label>Betalt til konto</label>
              <select className="form-control" value={paymentAccountId}
                onChange={e => setPaymentAccountId(e.target.value)}>
                <option value="">-- Vælg konto --</option>
                {paymentAccounts.map(a => (
                  <option key={a.id} value={a.id}>{a.account_number} - {a.name}</option>
                ))}
              </select>
            </div>
            <div className="modal-actions">
              <button className="btn btn-secondary" onClick={() => setShowPayModal(false)}>Annuller</button>
              <button className="btn btn-primary" onClick={handlePay} disabled={paying}>
                {paying ? 'Registrerer...' : 'Registrer betaling'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
